export interface IUploadFileData {
  'Email Address': string;
  'First Name': string;
  'Last Name': string;
  'User Role': string;
  Billable: string;
  'Limit IP Address (Optional)': string;
  'Permission Group': string;
  'Workspace Name': string;
}

//function to validation bulk upload file headers.
export const validateHeaders = (
  headersRow: string[],
  expectedHeaders: string[]
): boolean => {
  if (headersRow.length !== expectedHeaders.length) {
    return false;
  }

  return expectedHeaders.every((header, index) => headersRow[index] === header);
};

// function to validate the row is empty or not.
export const isRowEmpty = (
  row: IUploadFileData,
  headers: string[]
): boolean => {
  const placeholderValues: Record<string, string> = {
    'Email Address': '',
    'First Name': '',
    'Last Name': '',
    'User Role': 'select one',
    Billable: 'select one',
    'Limit IP Address (Optional)': '',
    'Permission Group': 'select one',
    'Workspace Name': 'select one',
  };

  return headers.every((header) => {
    const value = row[header as keyof IUploadFileData];
    const normalizedValue =
      value === undefined || value === null ? '' : value.toString().trim();
    const placeholderValue = placeholderValues[header];
    if (placeholderValue === 'select one') {
      return normalizedValue === '' || normalizedValue === placeholderValue;
    } else {
      return normalizedValue === '';
    }
  });
};

//function to ensure all column data exists.
export const ensureFields = (data: IUploadFileData[]) => {
  return data.map((row: IUploadFileData) => {
    return {
      'Email Address': row['Email Address'] || '',
      'First Name': row['First Name'] || '',
      'Last Name': row['Last Name'] || '',
      'User Role': row['User Role'] || '',
      Billable: row['Billable'] || '',
      'Limit IP Address (Optional)': row['Limit IP Address (Optional)'] || '',
      'Permission Group': row['Permission Group'] || '',
      'Workspace Name': row['Workspace Name'] || '',
    };
  });
};

export const expectedHeaders = [
  'Email Address',
  'First Name',
  'Last Name',
  'User Role',
  'Billable',
  'Limit IP Address (Optional)',
  'Client Permission Group',
  'Project Permission Group',
  'Project',
  'Workspace',
];

export const trimAfterDot = (str: string) => {
  const dotIndex = str.indexOf(':');
  return dotIndex !== -1 ? str.slice(0, dotIndex) : str;
};

export const isBulkUploadErrorUser = (user: {
  isError?: boolean;
  errors?: unknown[];
}) =>
  user?.isError === true ||
  (Array.isArray(user?.errors) && user.errors.length > 0);

export const getErrorUsersFromInitialUpload = (
  users: Array<{isError?: boolean; errors?: unknown[]}> = []
) => users.filter(isBulkUploadErrorUser);

export const excludeResolvedBulkUploadErrorUsers = (
  errorUsers: Array<{
    isError?: boolean;
    errors?: unknown[];
    userImportTempId?: unknown;
  }> = [],
  currentUsers: Array<{
    isError?: boolean;
    errors?: unknown[];
    userImportTempId?: unknown;
  }> = []
) => {
  const resolvedIds = new Set(
    currentUsers
      .filter(
        (user) =>
          user.userImportTempId != null && !isBulkUploadErrorUser(user)
      )
      .map((user) => user.userImportTempId)
  );

  if (resolvedIds.size === 0) {
    return errorUsers;
  }

  return errorUsers.filter(
    (user) =>
      user.userImportTempId == null || !resolvedIds.has(user.userImportTempId)
  );
};

export interface BulkUploadSummary {
  totalErrors?: number;
  totalUsers?: number;
  totalErrorUsers?: number;
  totalSuccessUsers?: number;
  totalExistingUser?: number;
}

const readSummaryNumber = (
  summary: Record<string, unknown>,
  camelKey: string,
  pascalKey: string
) => {
  const value = summary[camelKey] ?? summary[pascalKey];
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const normalizeBulkUploadSummary = (
  summary?: Record<string, unknown> | null
): BulkUploadSummary => {
  if (!summary || typeof summary !== 'object') {
    return {};
  }

  return {
    totalErrorUsers: readSummaryNumber(
      summary,
      'totalErrorUsers',
      'TotalErrorUsers'
    ),
    totalErrors: readSummaryNumber(summary, 'totalErrors', 'TotalErrors'),
    totalSuccessUsers: readSummaryNumber(
      summary,
      'totalSuccessUsers',
      'TotalSuccessUsers'
    ),
    totalUsers: readSummaryNumber(summary, 'totalUsers', 'TotalUsers'),
    totalExistingUser: readSummaryNumber(
      summary,
      'totalExistingUser',
      'TotalExistingUser'
    ),
  };
};

export const hasBulkUploadSummaryData = (summary?: BulkUploadSummary) =>
  !!summary &&
  ((summary.totalUsers ?? 0) > 0 ||
    (summary.totalErrorUsers ?? 0) > 0 ||
    (summary.totalSuccessUsers ?? 0) > 0 ||
    (summary.totalExistingUser ?? 0) > 0 ||
    (summary.totalErrors ?? 0) > 0);
